// Stock (spec/13 §6.4, spec/04 §9; ADR-0040; F-FNB-07 … F-FNB-13, F-POS-11's inventory tie-in).
//
// The `inventory` module owns a venue's stock: its items (ingredients, retail items, supplies) with their pack units,
// the append-only movement ledger whose sum is what is on hand, deliveries, counts, waste, recipes and suppliers. Every
// route here is **venue-scoped** — `/v1/organizations/{o}/venues/{v}/inventory/…` — because each venue defines its own
// stock (the owner's decision 9):
//
//   GET  …/inventory/status                     is stock on here, and how many items are low, out or negative
//   GET  …/inventory/alerts                     the low, out and negative items and the dishes sold out by stock
//   GET  …/inventory/items                      the stock list (every item, with on hand and its state)
//   POST …/inventory/items                      a new ingredient or supply (retail items are made for you)
//   GET  …/inventory/items/{stockItemId}        one item
//   PATCH …/inventory/items/{stockItemId}       edit one item and its packs; archive it
//   GET  …/inventory/lookup?barcode=            the item (and pack) a scanned barcode names
//   GET  …/inventory/movements                  the movement history, newest first, cursor-paginated
//   GET/POST …/inventory/suppliers, PATCH …/suppliers/{id}
//   GET/POST …/inventory/receipts, GET …/receipts/{id}   deliveries
//   POST …/inventory/usage                      waste, staff meals, supply issues
//   POST …/inventory/adjustments                a manual correction with a note
//   GET/POST …/inventory/counts, GET …/counts/{id}, PUT …/counts/{id}/lines, POST …/counts/{id}/submit|cancel
//   GET  …/inventory/recipes                    every recipe at the venue
//   GET/PUT …/inventory/products/{productId}/recipes     a product's recipes at the venue (its base, variants, modifiers)
//   POST …/inventory/copy-from-venue            another venue's items, packs and recipes, definitions only
//   GET  …/inventory/reports/valuation|usage|variance|margins
//
// **Units.** Every quantity is an integer in the item's base unit — `each`, `g` or `ml` — never a float and never a
// pack: a pack ("5 kg pack" = 5 000 g) is how a quantity is *entered*, and the server turns it into base units.
// **Money** is integer minor units with an ISO code; an average unit cost is the exact pair `StockUnitCost` (an amount
// for a number of base units), never a decimal. The ₹ figures on the reads — average cost, value, a movement's value, a
// count's variance value, a recipe's cost — are `null` for a caller without `inventory:reports` (`x-clavian-unmasked-by`).
// **Off** (`inventory.enabled` false at the venue, the default): every write answers `409 inventory-disabled`.
//
// C# component names are prefixed `Stock…` so they do not collide with the station's own types.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { ProductIdSchema, ProductModifierIdSchema, ProductVariantIdSchema, SaleIdSchema, SaleRefundIdSchema, StationIdSchema, StockCountIdSchema, StockItemIdSchema, StockItemPackIdSchema, StockMovementIdSchema, StockReceiptIdSchema, StockRecipeIdSchema, StockSupplierIdSchema, VenueIdSchema, } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { MoneySchema, NonNegativeMoneySchema } from "../primitives/money.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { BasisPointsSchema, CurrencyCodeSchema, IdempotencyKeySchema, IsoDateTimeSchema, LocalDateSchema } from "../primitives/scalars.js";
import { VenueScopeParamsSchema } from "../primitives/scope.js";
import { NARROW_PERMISSION_EXTENSION, PERMISSION_EXTENSION, UNMASKED_BY_EXTENSION } from "./audit.js";
import { INVENTORY_ADJUST, INVENTORY_COUNT, INVENTORY_MANAGE, INVENTORY_READ, INVENTORY_RECEIVE, INVENTORY_REPORTS, INVENTORY_SUPPLIES, INVENTORY_WASTE, } from "../permissions/index.js";
// ---------------------------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------------------------
/**
 * What a stock item is (ADR-0040 §2, §5, §6): an **ingredient** a recipe uses (cheese, fries, a can of cola), a
 * **retail** item sold off the shelf one-to-one (made by the server for each retail product), or a **supply** the venue
 * uses itself (cleaning products, spare keyboards) — received, issued, counted, never sold.
 */
export const StockItemKindSchema = z
    .enum(['ingredient', 'retail', 'supply'])
    .openapi('StockItemKind', { description: 'What a stock item is: an ingredient, a retail item or a supply (ADR-0040).' });
/** The unit every quantity of an item is counted in: whole units, grams or millilitres (ADR-0040 §2). */
export const StockBaseUnitSchema = z
    .enum(['each', 'g', 'ml'])
    .openapi('StockBaseUnit', { description: 'The unit a stock item is counted in. Quantities are whole numbers of it.' });
/**
 * Where an item stands (spec/13 §6.4): `negative` — below zero, "Recount needed"; `out` — exactly zero; `low` — above
 * zero but below its par level; `ok` otherwise (an item with no par level is never `low`).
 */
export const StockLevelSchema = z
    .enum(['ok', 'low', 'out', 'negative'])
    .openapi('StockLevel', { description: 'An item’s state: ok, low (below par), out (zero) or negative (recount needed).' });
/**
 * What a movement is (ADR-0040 §2): `sale` and `refund` (from a paid sale and a refund of it), `receive` (a delivery),
 * `waste`, `staff_meal` and `issue` (usage), `count` (a count's correction), `adjust` (a manual correction) and
 * `transfer` (between locations; modelled, not made by any endpoint yet).
 */
export const StockMovementKindSchema = z
    .enum(['sale', 'refund', 'receive', 'waste', 'staff_meal', 'issue', 'count', 'adjust', 'transfer'])
    .openapi('StockMovementKind', { description: 'What a stock movement is (ADR-0040 §2).' });
/** The usage a person records (spec/13 §6.4 Waste): wasted, eaten by staff, or a supply put to use. */
export const StockUsageKindSchema = z
    .enum(['waste', 'staff_meal', 'issue'])
    .openapi('StockUsageKind', { description: 'Usage staff record: waste, a staff meal, or a supply issued.' });
/** Why something was wasted (spec/13 §6.4). Required on waste; not on a staff meal or an issue. */
export const StockWasteReasonSchema = z
    .enum(['expired', 'damaged', 'spilled', 'quality', 'other'])
    .openapi('StockWasteReason', { description: 'Why stock was wasted.' });
/** A **full** count lists every active item; a **spot** count the items chosen (F-FNB-09). */
export const StockCountKindSchema = z
    .enum(['full', 'spot'])
    .openapi('StockCountKind', { description: 'A full count (every active item) or a spot count (the items chosen).' });
/** A count is `open` while it is being counted, then `submitted` (its variances moved stock) or `cancelled`. */
export const StockCountStatusSchema = z
    .enum(['open', 'submitted', 'cancelled'])
    .openapi('StockCountStatus', { description: 'Where a count is: open, submitted or cancelled.' });
/** The most base units one line of a request may name: 100 000 000 (100 tonnes in grams). */
export const STOCK_QUANTITY_MAX = 100_000_000;
const NAME = z.string().trim().min(1).max(120);
const NOTE = z.string().trim().min(1).max(500);
const BARCODE = z.string().trim().min(1).max(64);
const SKU = z.string().trim().min(1).max(64);
/** A non-negative quantity in base units, as a request line names it. */
const QUANTITY = z.int().min(0).max(STOCK_QUANTITY_MAX);
/** A quantity on hand or after a movement: whole base units, and it may be negative (the owner's decision 7). */
const ON_HAND = z.int();
const BOOLEAN_PARAM = z.enum(['true', 'false']);
/** A name for one `each` unit, or its plural: "can", "cans". */
const UNIT_NAME = z.string().trim().min(1).max(24);
/**
 * An exact average unit cost (ADR-0040 §15): `amount` buys `quantity` base units. ₹200 for a 5 kg pack is
 * `{ amount: { amount: 20000, currency: 'INR' }, quantity: 5000 }` — 4 paise a gram. Show it per kg, per L or each by
 * multiplying in integers (`amount × 1000 ÷ quantity`, rounded half up once, for display only); never divide it into
 * a float and store that.
 */
export const StockUnitCostSchema = z
    .object({
    amount: NonNegativeMoneySchema,
    quantity: z.int().min(1),
})
    .openapi('StockUnitCost', { description: 'An exact unit cost: `amount` for `quantity` base units (ADR-0040 §15).' });
// ---------------------------------------------------------------------------------------------
// Items and packs (F-FNB-07, F-FNB-11, F-POS-11)
// ---------------------------------------------------------------------------------------------
/** A pack unit: how an item arrives and is counted — "5 kg pack" = 5 000 g, "Case of 24" = 24 each (ADR-0040 §2). */
export const StockItemPackSchema = z
    .object({
    id: StockItemPackIdSchema,
    name: NAME,
    /** How many base units one pack holds (≥ 1). */
    quantity: z.int().min(1).max(STOCK_QUANTITY_MAX),
    /** The pack's own barcode (a case's), which a scan when receiving or counting resolves to this pack; null for none. */
    barcode: BARCODE.nullable(),
    archived: z.boolean(),
})
    .openapi('StockItemPack');
/**
 * One pack in an item write. Packs are written as a whole set: one sent with the `id` it was read with keeps it; one
 * without is new; one no longer sent is archived (a receipt that named it keeps its snapshot).
 */
export const StockItemPackInputSchema = z
    .object({
    id: StockItemPackIdSchema.optional(),
    name: NAME,
    quantity: z.int().min(1).max(STOCK_QUANTITY_MAX),
    barcode: BARCODE.nullable().optional(),
})
    .openapi('StockItemPackInput');
/** A stock item with where it stands now (spec/13 §6.4 Stock list). */
export const StockItemSchema = z
    .object({
    id: StockItemIdSchema,
    name: NAME,
    kind: StockItemKindSchema,
    baseUnit: StockBaseUnitSchema,
    sku: SKU.nullable(),
    /** The item's own barcode; a retail item is also found by its product's barcode. Null for none. */
    barcode: BARCODE.nullable(),
    /** The retail product this item tracks one-to-one (ADR-0040 §6); null for an ingredient or a supply. */
    productId: ProductIdSchema.nullable(),
    /** Below this many base units the item is `low`; null for no par level. */
    parLevel: z.int().min(0).nullable(),
    note: NOTE.nullable(),
    /**
     * What one `each` unit is called, singular and plural ("can", "cans"), so a screen can say "12 cans" rather than
     * "12 pcs". Null for none (the screen says "each"); not used for `g` and `ml`.
     */
    unitName: UNIT_NAME.nullable(),
    unitNamePlural: UNIT_NAME.nullable(),
    /** Who the venue usually buys it from — what a delivery of it pre-selects. Null for none. */
    supplierId: StockSupplierIdSchema.nullable(),
    packs: z.array(StockItemPackSchema),
    /** What is on hand at the venue, in base units — the sum of its movements. May be negative. */
    onHand: ON_HAND,
    level: StockLevelSchema,
    /** The exact weighted-average cost (ADR-0040 §15). Null when never costed, or without `inventory:reports`. */
    averageUnitCost: StockUnitCostSchema.nullable(),
    /** What the stock on hand is worth, in minor units. Null without `inventory:reports`. */
    value: MoneySchema.nullable(),
    lastMovementAt: IsoDateTimeSchema.nullable(),
    lastCountedAt: IsoDateTimeSchema.nullable(),
    archived: z.boolean(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('StockItem');
export const StockItemListSchema = z
    .object({ items: z.array(StockItemSchema) })
    .openapi('StockItemList', { description: 'A venue’s stock items, by name. Every active item; archived ones on request.' });
export const StockItemListQuerySchema = z
    .object({
    kind: StockItemKindSchema.optional(),
    /** Only items in this state (`low` returns the low ones; `negative` those below zero). */
    level: StockLevelSchema.optional(),
    /** A case-insensitive match on the name, SKU or barcode. */
    q: z.string().trim().min(1).max(80).optional(),
    includeArchived: BOOLEAN_PARAM.optional(),
})
    .openapi('StockItemListQuery');
/**
 * A new ingredient or supply. A retail item is not created here — the server makes one for each retail product while
 * stock is on (ADR-0040 §6) — so `kind` is `ingredient` or `supply`; `retail` is refused (400). With
 * `inventory:supplies` and not `inventory:manage`, only a `supply`.
 */
export const CreateStockItemSchema = z
    .object({
    name: NAME,
    kind: StockItemKindSchema,
    baseUnit: StockBaseUnitSchema,
    sku: SKU.nullable().optional(),
    barcode: BARCODE.nullable().optional(),
    parLevel: z.int().min(0).max(STOCK_QUANTITY_MAX).nullable().optional(),
    note: NOTE.nullable().optional(),
    /** What one `each` unit is called ("can"), and its plural ("cans"); refused (400) on a `g` or `ml` item. */
    unitName: UNIT_NAME.nullable().optional(),
    unitNamePlural: UNIT_NAME.nullable().optional(),
    /** The venue's usual supplier of it (one of `GET …/inventory/suppliers`), or null. */
    supplierId: StockSupplierIdSchema.nullable().optional(),
    packs: z.array(StockItemPackInputSchema).max(20).optional(),
})
    .openapi('CreateStockItemRequest');
/**
 * Edit an item: the fields given. `baseUnit` can change only while the item has never moved (409 otherwise); `packs`
 * replaces the set (see `StockItemPackInput`); `archived: true` hides it from the list and from counts, its history
 * kept. A retail item's name follows its product, so `name` on one is refused (400).
 */
export const UpdateStockItemSchema = z
    .object({
    name: NAME.optional(),
    baseUnit: StockBaseUnitSchema.optional(),
    sku: SKU.nullable().optional(),
    barcode: BARCODE.nullable().optional(),
    parLevel: z.int().min(0).max(STOCK_QUANTITY_MAX).nullable().optional(),
    note: NOTE.nullable().optional(),
    unitName: UNIT_NAME.nullable().optional(),
    unitNamePlural: UNIT_NAME.nullable().optional(),
    supplierId: StockSupplierIdSchema.nullable().optional(),
    packs: z.array(StockItemPackInputSchema).max(20).optional(),
    archived: z.boolean().optional(),
})
    .openapi('UpdateStockItemRequest');
/** What a scanned barcode names (spec/13 §6.4): the item, and the pack when it was a pack's barcode. */
export const StockLookupSchema = z
    .object({
    item: StockItemSchema,
    /** The pack whose barcode was scanned; null when it was the item's (or its retail product's) own barcode. */
    pack: StockItemPackSchema.nullable(),
})
    .openapi('StockLookup');
export const StockLookupQuerySchema = z.object({ barcode: BARCODE }).openapi('StockLookupQuery');
// ---------------------------------------------------------------------------------------------
// Movements (F-FNB-08)
// ---------------------------------------------------------------------------------------------
/** One change to what is on hand (ADR-0040 §2). Never edited; a correction is a new movement. */
export const StockMovementSchema = z
    .object({
    id: StockMovementIdSchema,
    stockItemId: StockItemIdSchema,
    kind: StockMovementKindSchema,
    /** The change, in base units: negative takes stock away, positive adds it. Never zero. */
    quantity: z.int(),
    /** On hand just after this movement, in base units. */
    quantityAfter: ON_HAND,
    /** The value that moved, signed like `quantity`, in minor units (ADR-0040 §15). Null without `inventory:reports`. */
    value: MoneySchema.nullable(),
    /** A waste's reason; null for every other kind. */
    reason: StockWasteReasonSchema.nullable(),
    note: NOTE.nullable(),
    /** The sale a `sale` or `refund` movement came from. */
    saleId: SaleIdSchema.nullable(),
    /** The refund a `refund` movement came from. */
    refundId: SaleRefundIdSchema.nullable(),
    /** The delivery a `receive` movement came from. */
    receiptId: StockReceiptIdSchema.nullable(),
    /** The count a `count` movement came from. */
    countId: StockCountIdSchema.nullable(),
    /** The product sold, refunded or wasted by dish, when the movement came from one. */
    productId: ProductIdSchema.nullable(),
    /** The PC a supply was issued to, when one was named. */
    stationId: StationIdSchema.nullable(),
    /** Who recorded it, as their name read when it was recorded; null for the system (a sale, a refund). */
    recordedByName: z.string().nullable(),
    occurredAt: IsoDateTimeSchema,
})
    .openapi('StockMovement');
export const StockMovementListSchema = paginated(StockMovementSchema, 'StockMovementList');
export const StockMovementListQuerySchema = PageQuerySchema.extend({
    /** One item's history; absent, every item's. */
    stockItemId: StockItemIdSchema.optional(),
    kind: StockMovementKindSchema.optional(),
    /** Movements at or after this instant. */
    from: IsoDateTimeSchema.optional(),
    /** Movements before this instant. */
    to: IsoDateTimeSchema.optional(),
    /**
     * Movements from the start of this business day of the venue (its timezone and cutoff, so "today" at 02:00 is the
     * day that began yesterday at 06:00). Combined with `from`, the later of the two applies.
     */
    fromDay: LocalDateSchema.optional(),
    /** Movements to the end of this business day of the venue (inclusive). Combined with `to`, the earlier applies. */
    toDay: LocalDateSchema.optional(),
}).openapi('StockMovementListQuery');
// ---------------------------------------------------------------------------------------------
// Suppliers (F-FNB-09)
// ---------------------------------------------------------------------------------------------
export const StockSupplierSchema = z
    .object({
    id: StockSupplierIdSchema,
    name: NAME,
    phone: z.string().trim().min(3).max(30).nullable(),
    email: z.email().max(254).nullable(),
    note: NOTE.nullable(),
    archived: z.boolean(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('StockSupplier');
export const StockSupplierListSchema = z.object({ suppliers: z.array(StockSupplierSchema) }).openapi('StockSupplierList');
export const StockSupplierListQuerySchema = z.object({ includeArchived: BOOLEAN_PARAM.optional() }).openapi('StockSupplierListQuery');
export const CreateStockSupplierSchema = z
    .object({
    name: NAME,
    phone: z.string().trim().min(3).max(30).nullable().optional(),
    email: z.email().max(254).nullable().optional(),
    note: NOTE.nullable().optional(),
})
    .openapi('CreateStockSupplierRequest');
export const UpdateStockSupplierSchema = CreateStockSupplierSchema.partial()
    .extend({ archived: z.boolean().optional() })
    .openapi('UpdateStockSupplierRequest');
// ---------------------------------------------------------------------------------------------
// Quantities as entered: packs and loose units
// ---------------------------------------------------------------------------------------------
/** Some number of one of the item's packs. */
export const StockPackCountInputSchema = z
    .object({ packId: StockItemPackIdSchema, count: z.int().min(0).max(100_000) })
    .openapi('StockPackCountInput');
// ---------------------------------------------------------------------------------------------
// Receiving (F-FNB-09)
// ---------------------------------------------------------------------------------------------
/**
 * One line of a delivery: an item, how much of it — packs, loose base units, or both, summed — and what the line cost
 * in total. The quantity must come to more than zero.
 */
export const StockReceiptLineInputSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    /** Loose base units, on top of any packs. */
    quantity: QUANTITY.optional(),
    packs: z.array(StockPackCountInputSchema).max(10).optional(),
    /** What the whole line cost, in minor units (the invoice line's amount). Zero for a free sample. */
    cost: NonNegativeMoneySchema,
})
    .openapi('StockReceiptLineInput');
export const CreateStockReceiptSchema = z
    .object({
    supplierId: StockSupplierIdSchema.optional(),
    /** The supplier's invoice or delivery-note number. */
    reference: z.string().trim().min(1).max(80).optional(),
    note: NOTE.optional(),
    /** When it arrived; defaults to now, and may not be in the future. */
    receivedAt: IsoDateTimeSchema.optional(),
    lines: z.array(StockReceiptLineInputSchema).min(1).max(200),
})
    .openapi('CreateStockReceiptRequest');
/** A pack on a delivery line, as it was when received (a snapshot). */
export const StockReceiptLinePackSchema = z
    .object({
    packId: StockItemPackIdSchema,
    name: NAME,
    /** Base units in one pack, then. */
    quantity: z.int().min(1),
    count: z.int().min(0),
})
    .openapi('StockReceiptLinePack');
/** One line of a delivery, as received (a snapshot). */
export const StockReceiptLineSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    baseUnit: StockBaseUnitSchema,
    packs: z.array(StockReceiptLinePackSchema),
    /** The loose base units entered beside the packs. */
    looseQuantity: z.int().min(0),
    /** The whole line in base units: packs × their size plus the loose units. */
    quantity: z.int().min(1),
    cost: MoneySchema,
})
    .openapi('StockReceiptLine');
/** A delivery received into stock (F-FNB-09). Never edited; a mistake is corrected with an adjustment. */
export const StockReceiptSchema = z
    .object({
    id: StockReceiptIdSchema,
    supplierId: StockSupplierIdSchema.nullable(),
    /** The supplier's name as it was when received. */
    supplierName: z.string().nullable(),
    reference: z.string().nullable(),
    note: NOTE.nullable(),
    receivedAt: IsoDateTimeSchema,
    recordedByName: z.string().nullable(),
    totalCost: MoneySchema,
    lines: z.array(StockReceiptLineSchema),
    createdAt: IsoDateTimeSchema,
})
    .openapi('StockReceipt');
export const StockReceiptListSchema = paginated(StockReceiptSchema, 'StockReceiptList');
// ---------------------------------------------------------------------------------------------
// Usage: waste, staff meals, supply issues (F-FNB-09, F-FNB-11)
// ---------------------------------------------------------------------------------------------
/**
 * One line of usage: **either** an item (`stockItemId`, with `quantity` and/or `packs`) **or** a dish (`productId`,
 * with its `variantId` and `modifierIds` and a number of `servings`), whose recipe at the venue is taken away. A line
 * naming both, or neither, is refused (400).
 */
export const StockUsageLineInputSchema = z
    .object({
    stockItemId: StockItemIdSchema.optional(),
    quantity: QUANTITY.optional(),
    packs: z.array(StockPackCountInputSchema).max(10).optional(),
    productId: ProductIdSchema.optional(),
    variantId: ProductVariantIdSchema.optional(),
    modifierIds: z.array(ProductModifierIdSchema).max(20).optional(),
    servings: z.int().min(1).max(1000).optional(),
})
    .openapi('StockUsageLineInput');
export const CreateStockUsageSchema = z
    .object({
    kind: StockUsageKindSchema,
    /** Required for `waste`; refused on the other kinds. */
    reason: StockWasteReasonSchema.optional(),
    note: NOTE.optional(),
    /** The PC a supply was issued to (`issue` only). */
    stationId: StationIdSchema.optional(),
    lines: z.array(StockUsageLineInputSchema).min(1).max(100),
})
    .openapi('CreateStockUsageRequest');
/** The movements one usage entry made — one per item it took away. */
export const StockUsageResultSchema = z
    .object({ movements: z.array(StockMovementSchema) })
    .openapi('StockUsageResult');
// ---------------------------------------------------------------------------------------------
// Adjustments (F-FNB-09)
// ---------------------------------------------------------------------------------------------
/** A manual correction to one item, with the reason in words. */
export const CreateStockAdjustmentSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    /** The change in base units: negative takes stock away, positive adds it. Never zero. */
    quantity: z
        .int()
        .min(-STOCK_QUANTITY_MAX)
        .max(STOCK_QUANTITY_MAX)
        .refine((value) => value !== 0, { message: 'An adjustment changes the quantity.' }),
    note: NOTE,
})
    .openapi('CreateStockAdjustmentRequest');
// ---------------------------------------------------------------------------------------------
// Counts (F-FNB-09)
// ---------------------------------------------------------------------------------------------
/** A pack on a count line, as it was entered. */
export const StockCountLinePackSchema = z
    .object({
    packId: StockItemPackIdSchema,
    name: NAME,
    /** Base units in one pack, when it was entered. */
    quantity: z.int().min(1),
    count: z.int().min(0),
})
    .openapi('StockCountLinePack');
/** What was typed for a line — packs and loose base units — so a count resumed elsewhere shows it the same way. */
export const StockCountEntrySchema = z
    .object({
    /** Loose base units, beside the packs. */
    quantity: z.int().min(0),
    packs: z.array(StockCountLinePackSchema),
})
    .openapi('StockCountEntry');
/**
 * One item on a count. `expected` is what was on hand **at the moment the line was counted** — snapshot then, so a
 * sale while the count is still open does not shift the variance of a shelf already counted; for a line not counted
 * yet it is what is on hand now. Submitting moves each counted item by its `variance`, so whatever moved after its
 * shelf was counted still counts.
 */
export const StockCountLineSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    baseUnit: StockBaseUnitSchema,
    /** What was counted, in base units; null until counted. */
    counted: z.int().min(0).nullable(),
    /** What was entered for `counted` (packs and loose units); null until counted. */
    entered: StockCountEntrySchema.nullable(),
    /** When the line was counted; null until it is. */
    countedAt: IsoDateTimeSchema.nullable(),
    expected: ON_HAND,
    /** `counted − expected`; null until counted. */
    variance: z.int().nullable(),
    /** The variance's value in minor units. Null until counted, when the item was never costed, or without `inventory:reports`. */
    varianceValue: MoneySchema.nullable(),
})
    .openapi('StockCountLine');
const StockCountFields = {
    id: StockCountIdSchema,
    kind: StockCountKindSchema,
    status: StockCountStatusSchema,
    note: NOTE.nullable(),
    startedAt: IsoDateTimeSchema,
    startedByName: z.string().nullable(),
    submittedAt: IsoDateTimeSchema.nullable(),
    submittedByName: z.string().nullable(),
    cancelledAt: IsoDateTimeSchema.nullable(),
    /** The note given when it was submitted; null for none. */
    submitNote: NOTE.nullable(),
    /** How many items are on the count, and how many have been counted. */
    lineCount: z.int().min(0),
    countedCount: z.int().min(0),
    /** The count's net variance in minor units: once submitted, or a preview while open. Null without `inventory:reports`. */
    varianceValue: MoneySchema.nullable(),
};
/** A count without its lines, for the list. */
export const StockCountSummarySchema = z.object(StockCountFields).openapi('StockCountSummary');
/** A count with its lines — the variance review (spec/13 §6.4 Count). */
export const StockCountSchema = z
    .object({ ...StockCountFields, lines: z.array(StockCountLineSchema) })
    .openapi('StockCount');
export const StockCountListSchema = paginated(StockCountSummarySchema, 'StockCountList');
export const StockCountListQuerySchema = PageQuerySchema.extend({ status: StockCountStatusSchema.optional() }).openapi('StockCountListQuery');
/**
 * Start a count. `full` puts every active item of the venue on it (with `inventory:supplies` alone, a count is a
 * `spot` of supplies); `spot` the `stockItemIds` given (required for a spot count).
 */
export const CreateStockCountSchema = z
    .object({
    kind: StockCountKindSchema,
    note: NOTE.optional(),
    stockItemIds: z.array(StockItemIdSchema).min(1).max(1000).optional(),
})
    .openapi('CreateStockCountRequest');
/**
 * Counted quantities for some items on an open count: packs and/or loose base units, summed; `clear: true` forgets a
 * line's figure. An item not yet on a spot count is added to it; a full count already lists every item.
 */
export const StockCountLineInputSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    quantity: QUANTITY.optional(),
    packs: z.array(StockPackCountInputSchema).max(10).optional(),
    clear: z.boolean().optional(),
})
    .openapi('StockCountLineInput');
export const PutStockCountLinesSchema = z
    .object({ lines: z.array(StockCountLineInputSchema).min(1).max(1000) })
    .openapi('PutStockCountLinesRequest');
/** Submit a count, optionally with a note on what was found ("Two cases behind the fridge"). */
export const SubmitStockCountSchema = z.object({ note: NOTE.optional() }).openapi('SubmitStockCountRequest');
// ---------------------------------------------------------------------------------------------
// Recipes (F-FNB-07)
// ---------------------------------------------------------------------------------------------
/** One stock item a recipe takes, per one unit sold. */
export const StockRecipeComponentSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    baseUnit: StockBaseUnitSchema,
    quantity: z.int().min(1),
    /** This component's cost at the item's average, rounded once. Null when never costed, or without `inventory:reports`. */
    cost: MoneySchema.nullable(),
})
    .openapi('StockRecipeComponent');
/**
 * What one unit of a product (its base recipe — both ids null), of one of its variants, or of one of its modifiers
 * takes from stock at this venue (ADR-0040 §3). A variant with no recipe of its own uses the product's base recipe;
 * a modifier's recipe is added per unit of the line it is chosen on.
 */
export const StockRecipeSchema = z
    .object({
    id: StockRecipeIdSchema,
    productId: ProductIdSchema,
    variantId: ProductVariantIdSchema.nullable(),
    modifierId: ProductModifierIdSchema.nullable(),
    /** What it is the recipe of, in words: "Fries", "Fries — Large", "Extra cheese". */
    name: z.string(),
    components: z.array(StockRecipeComponentSchema),
    /** The recipe's cost, summed exactly and rounded once (ADR-0040 §15). Null without `inventory:reports`. */
    cost: MoneySchema.nullable(),
    /** False when a component has never been costed, so `cost` is a floor, not the cost. */
    costComplete: z.boolean(),
    /**
     * What it sells for at this venue: the product's price for its base recipe, the variant's for a variant, the extra's
     * own price for a modifier.
     */
    price: MoneySchema,
    /** `price` excluding tax, under the venue's own tax treatment (what the margin is taken from). */
    priceExclTax: MoneySchema,
    /** `priceExclTax − cost`. Null without `inventory:reports`. */
    margin: MoneySchema.nullable(),
    /** The margin in basis points of `priceExclTax`; null when that is zero or without `inventory:reports`. */
    marginBps: BasisPointsSchema.nullable(),
    /** Whether one unit can be made from what is on hand now. */
    makeable: z.boolean(),
})
    .openapi('StockRecipe');
export const StockRecipeListSchema = z.object({ recipes: z.array(StockRecipeSchema) }).openapi('StockRecipeList');
/**
 * One sellable unit of a product — the product itself, or each of its variants — with the recipe it takes away (its own,
 * else the product's base recipe, else its retail item one-to-one) and what it earns. Extras are on `recipes`.
 */
export const StockSellableUnitSchema = z
    .object({
    /** Null for a product with no variants. */
    variantId: ProductVariantIdSchema.nullable(),
    /** "Fries — Large", or the product's name. */
    name: z.string(),
    /** The recipe this unit takes; null when it takes the retail item one-to-one or is not tracked. */
    recipeId: StockRecipeIdSchema.nullable(),
    /** False when nothing in stock tracks this unit. */
    tracked: z.boolean(),
    price: MoneySchema,
    priceExclTax: MoneySchema,
    /** Null without `inventory:reports` or when not tracked. */
    cost: MoneySchema.nullable(),
    costComplete: z.boolean(),
    margin: MoneySchema.nullable(),
    marginBps: BasisPointsSchema.nullable(),
    makeable: z.boolean(),
})
    .openapi('StockSellableUnit');
/** A product's recipes at the venue — the product dialog's Stock tab (spec/13 §6.4). */
export const StockProductRecipesSchema = z
    .object({
    productId: ProductIdSchema,
    productName: NAME,
    /** The retail item that tracks it one-to-one when it has no recipe (ADR-0040 §6); null for food and drink. */
    retailItemId: StockItemIdSchema.nullable(),
    /** True when the venue's sold-out switch is off because stock cannot make it (F-FNB-10). */
    soldOutForStock: z.boolean(),
    recipes: z.array(StockRecipeSchema),
    /** Each variant (or the product) with its effective recipe's cost and margin — one row per thing a player buys. */
    units: z.array(StockSellableUnitSchema),
})
    .openapi('StockProductRecipes');
/** One recipe in a write: what it is the recipe of (both ids absent: the product's base recipe), and its components. */
export const StockRecipeInputSchema = z
    .object({
    variantId: ProductVariantIdSchema.optional(),
    modifierId: ProductModifierIdSchema.optional(),
    components: z
        .array(z.object({ stockItemId: StockItemIdSchema, quantity: z.int().min(1).max(STOCK_QUANTITY_MAX) }).openapi('StockRecipeComponentInput'))
        .min(1)
        .max(50),
})
    .openapi('StockRecipeInput');
/**
 * Replace every recipe of one product at this venue: those sent are written, any other is removed. A variant and a
 * modifier must be the product's; one recipe per target; an item at most once per recipe; a retail or supply item may
 * be a component, an archived one may not.
 */
export const PutStockProductRecipesSchema = z
    .object({ recipes: z.array(StockRecipeInputSchema).max(200) })
    .openapi('PutStockProductRecipesRequest');
// ---------------------------------------------------------------------------------------------
// Copy from another venue (F-FNB-13)
// ---------------------------------------------------------------------------------------------
/**
 * Copy another venue's items, packs and recipes here (ADR-0040 §9). `dryRun: true` writes nothing and answers exactly
 * what a copy would do — so the console previews it with this venue's `inventory:manage`, needing no read at the source.
 */
export const CopyStockFromVenueSchema = z
    .object({ sourceVenueId: VenueIdSchema, dryRun: z.boolean().optional() })
    .openapi('CopyStockFromVenueRequest');
/**
 * What became (or, in a dry run, would become) of one of the source's active items: `copy` — made here; `match` — this
 * venue already has an active item of the same name (case-insensitive) and unit, which the copied recipes use instead
 * of a duplicate; `already_copied` — copied here before; `skip_retail` — a retail item, linked one-to-one to its product
 * at each venue, never copied; `skip_unit` — an item of the same name here counts in another unit, so neither is
 * copied over the other.
 */
export const StockCopyOutcomeSchema = z
    .enum(['copy', 'match', 'already_copied', 'skip_retail', 'skip_unit'])
    .openapi('StockCopyOutcome', { description: 'What a copy does with one source item (ADR-0040 §9).' });
export const StockCopyItemSchema = z
    .object({
    name: NAME,
    kind: StockItemKindSchema,
    baseUnit: StockBaseUnitSchema,
    outcome: StockCopyOutcomeSchema,
    /** The item here it became, matched or was copied to before; null for a skipped one, and for a new one in a dry run. */
    stockItemId: StockItemIdSchema.nullable(),
})
    .openapi('StockCopyItem');
/** What became of one of the source's recipes: `copy`, `already_here` (this target has a recipe here), or `skip` (none of its items could come across). */
export const StockCopyRecipeOutcomeSchema = z
    .enum(['copy', 'already_here', 'skip'])
    .openapi('StockCopyRecipeOutcome', { description: 'What a copy does with one source recipe (ADR-0040 §9).' });
export const StockCopyRecipeSchema = z
    .object({
    productId: ProductIdSchema,
    variantId: ProductVariantIdSchema.nullable(),
    modifierId: ProductModifierIdSchema.nullable(),
    name: z.string(),
    outcome: StockCopyRecipeOutcomeSchema,
})
    .openapi('StockCopyRecipe');
/** What a copy did — or would do, with `dryRun`. Running it again copies nothing twice (ADR-0040 §9). */
export const StockCopyResultSchema = z
    .object({
    dryRun: z.boolean(),
    itemsCopied: z.int().min(0),
    /** Source items matched to one here by name and unit. */
    itemsMatched: z.int().min(0),
    /** Items of the source already copied here before (left as they are). */
    itemsAlreadyHere: z.int().min(0),
    /** Retail items (never copied) and same-name items in another unit. */
    itemsSkipped: z.int().min(0),
    recipesCopied: z.int().min(0),
    /** Recipes whose product, variant or modifier already has a recipe here (left as they are). */
    recipesAlreadyHere: z.int().min(0),
    /** Recipes none of whose items could come across. */
    recipesSkipped: z.int().min(0),
    items: z.array(StockCopyItemSchema),
    recipes: z.array(StockCopyRecipeSchema),
})
    .openapi('StockCopyResult');
// ---------------------------------------------------------------------------------------------
// Status and alerts (F-FNB-10)
// ---------------------------------------------------------------------------------------------
/** Whether stock is on at the venue, and the counts the Stock screen and "What needs you" show. */
export const StockStatusSchema = z
    .object({
    /** `inventory.enabled` at the venue. Off, every write is refused and the console hides Stock. */
    enabled: z.boolean(),
    /** `fnb.autoUnavailableOnZeroStock` at the venue. */
    autoUnavailableOnZeroStock: z.boolean(),
    itemCount: z.int().min(0),
    lowCount: z.int().min(0),
    outCount: z.int().min(0),
    negativeCount: z.int().min(0),
    /** Products switched off at the venue by stock. */
    soldOutCount: z.int().min(0),
    /** When the last full count was submitted; null before the first — what staff do first (spec/13 §6.4). */
    lastFullCountAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StockStatus');
/** An item that needs someone: low, out or below zero. */
export const StockAlertItemSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    kind: StockItemKindSchema,
    baseUnit: StockBaseUnitSchema,
    onHand: ON_HAND,
    parLevel: z.int().min(0).nullable(),
    level: StockLevelSchema,
})
    .openapi('StockAlertItem');
/** A product switched off at the venue because its recipes cannot be made (F-FNB-10). */
export const StockSoldOutProductSchema = z
    .object({ productId: ProductIdSchema, name: NAME })
    .openapi('StockSoldOutProduct');
/** What "What needs you" shows for stock (spec/13 §3.2, §6.4). Every list is empty while stock is off. */
export const StockAlertsSchema = z
    .object({
    enabled: z.boolean(),
    low: z.array(StockAlertItemSchema),
    out: z.array(StockAlertItemSchema),
    /** Below zero: "Recount needed". */
    negative: z.array(StockAlertItemSchema),
    soldOut: z.array(StockSoldOutProductSchema),
})
    .openapi('StockAlerts');
// ---------------------------------------------------------------------------------------------
// Reports (F-FNB-12)
// ---------------------------------------------------------------------------------------------
/** A range of the venue's business days, both inclusive, in the venue's timezone and cutoff. At most 92 days. */
export const StockReportRangeQuerySchema = z
    .object({ from: LocalDateSchema, to: LocalDateSchema })
    .openapi('StockReportRangeQuery');
/** The instants a range of business days covers. */
export const StockReportWindowSchema = z
    .object({ from: LocalDateSchema, to: LocalDateSchema, startsAt: IsoDateTimeSchema, endsAt: IsoDateTimeSchema })
    .openapi('StockReportWindow');
export const StockValuationLineSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    kind: StockItemKindSchema,
    baseUnit: StockBaseUnitSchema,
    onHand: ON_HAND,
    averageUnitCost: StockUnitCostSchema.nullable(),
    value: MoneySchema,
})
    .openapi('StockValuationLine');
/** What the stock on hand is worth now, by item (spec/13 §6.4 Reports). */
export const StockValuationReportSchema = z
    .object({
    currency: CurrencyCodeSchema,
    total: MoneySchema,
    asOf: IsoDateTimeSchema,
    items: z.array(StockValuationLineSchema),
})
    .openapi('StockValuationReport');
/** One kind of movement's total for an item over the range. */
export const StockUsageByKindSchema = z
    .object({ kind: StockMovementKindSchema, quantity: z.int(), value: MoneySchema })
    .openapi('StockUsageByKind');
export const StockUsageLineSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    kind: StockItemKindSchema,
    baseUnit: StockBaseUnitSchema,
    /** The item's movements over the range by kind, signed (sold and wasted are negative). Kinds with none are left out. */
    byKind: z.array(StockUsageByKindSchema),
})
    .openapi('StockUsageLine');
/** What was sold, wasted, eaten, issued, received, counted and adjusted over a range of business days. */
export const StockUsageReportSchema = z
    .object({
    window: StockReportWindowSchema,
    currency: CurrencyCodeSchema,
    items: z.array(StockUsageLineSchema),
    /** The value of each kind across every item. */
    totals: z.array(z.object({ kind: StockMovementKindSchema, value: MoneySchema }).openapi('StockUsageTotal')),
})
    .openapi('StockUsageReport');
export const StockVarianceCountSchema = z
    .object({
    countId: StockCountIdSchema,
    kind: StockCountKindSchema,
    submittedAt: IsoDateTimeSchema,
    submittedByName: z.string().nullable(),
    lineCount: z.int().min(0),
    varianceValue: MoneySchema,
})
    .openapi('StockVarianceCount');
export const StockVarianceItemSchema = z
    .object({
    stockItemId: StockItemIdSchema,
    name: NAME,
    baseUnit: StockBaseUnitSchema,
    varianceQuantity: z.int(),
    varianceValue: MoneySchema,
})
    .openapi('StockVarianceItem');
/** The counts submitted over a range and what they found, in units and ₹ (spec/13 §6.4 Reports). */
export const StockVarianceReportSchema = z
    .object({
    window: StockReportWindowSchema,
    currency: CurrencyCodeSchema,
    counts: z.array(StockVarianceCountSchema),
    items: z.array(StockVarianceItemSchema),
    total: MoneySchema,
})
    .openapi('StockVarianceReport');
/**
 * One product (or one of its variants) with a recipe at the venue: its price, its price excluding tax under the
 * venue's own tax treatment, its recipe's cost, and the margin between them (ADR-0040 §15).
 */
export const StockMarginLineSchema = z
    .object({
    productId: ProductIdSchema,
    variantId: ProductVariantIdSchema.nullable(),
    name: z.string(),
    price: MoneySchema,
    priceExclTax: MoneySchema,
    cost: MoneySchema,
    /** False when an ingredient was never costed: `cost` is then a floor and the margin a ceiling. */
    costComplete: z.boolean(),
    /** `priceExclTax − cost`. */
    margin: MoneySchema,
    /** The margin as basis points of `priceExclTax`, rounded half up; null when the price excluding tax is zero. */
    marginBps: BasisPointsSchema.nullable(),
})
    .openapi('StockMarginLine');
export const StockMarginsReportSchema = z
    .object({ currency: CurrencyCodeSchema, asOf: IsoDateTimeSchema, products: z.array(StockMarginLineSchema) })
    .openapi('StockMarginsReport');
// ---------------------------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------------------------
const BASE = '/v1/organizations/{organizationId}/venues/{venueId}/inventory';
const OFF = problemResponse('Stock tracking is off at this venue.', 'inventory-disabled');
const ITEM_PARAMS = VenueScopeParamsSchema.extend({ stockItemId: StockItemIdSchema }).openapi('StockItemParams');
const SUPPLIER_PARAMS = VenueScopeParamsSchema.extend({ supplierId: StockSupplierIdSchema }).openapi('StockSupplierParams');
const RECEIPT_PARAMS = VenueScopeParamsSchema.extend({ receiptId: StockReceiptIdSchema }).openapi('StockReceiptParams');
const COUNT_PARAMS = VenueScopeParamsSchema.extend({ countId: StockCountIdSchema }).openapi('StockCountParams');
const PRODUCT_PARAMS = VenueScopeParamsSchema.extend({ productId: ProductIdSchema }).openapi('StockProductParams');
const SUPPLIES_NOTE = ' With `inventory:supplies` alone, supply items only (403 otherwise).';
/**
 * The `Idempotency-Key` the stock-changing POSTs **require** (a delivery, usage, an adjustment, a count's submit): a
 * double-tap must never post twice. A retry with the same key and body is answered from the first answer
 * (`Idempotent-Replayed: true`); the same key with another body is `422 idempotency-key-reused`; no key is `400`.
 */
export const StockIdempotencyHeaderSchema = z
    .object({ 'idempotency-key': IdempotencyKeySchema })
    .openapi('StockIdempotencyHeaders');
const reads = {
    400: commonProblemResponses[400],
    401: commonProblemResponses[401],
    403: commonProblemResponses[403],
    429: commonProblemResponses[429],
    500: commonProblemResponses[500],
};
export const getStockStatusRoute = createRoute({
    method: 'get',
    path: `${BASE}/status`,
    operationId: 'getStockStatus',
    tags: ['inventory'],
    summary: 'Is stock tracked here, and how many items need someone (F-FNB-10)',
    description: 'Whether `inventory.enabled` is on at the venue, and the low, out, negative and sold-out counts. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    request: { params: VenueScopeParamsSchema },
    responses: { 200: jsonResponse(StockStatusSchema, 'The venue’s stock status.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const getStockAlertsRoute = createRoute({
    method: 'get',
    path: `${BASE}/alerts`,
    operationId: 'getStockAlerts',
    tags: ['inventory'],
    summary: 'Low, out and negative items, and dishes sold out by stock (F-FNB-10)',
    description: 'What "What needs you" shows for stock (spec/13 §3.2). Empty lists while stock is off. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    request: { params: VenueScopeParamsSchema },
    responses: { 200: jsonResponse(StockAlertsSchema, 'The venue’s stock alerts.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const listStockItemsRoute = createRoute({
    method: 'get',
    path: `${BASE}/items`,
    operationId: 'listStockItems',
    tags: ['inventory'],
    summary: 'The stock list (F-FNB-07)',
    description: 'Every active stock item at the venue, by name, with on hand and its state; archived ones with `includeArchived=true`. ' +
        'Not paginated: a venue’s list is bounded, like its menu. The ₹ figures need `inventory:reports`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, query: StockItemListQuerySchema },
    responses: { 200: jsonResponse(StockItemListSchema, 'The venue’s stock items.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const createStockItemRoute = createRoute({
    method: 'post',
    path: `${BASE}/items`,
    operationId: 'createStockItem',
    tags: ['inventory'],
    summary: 'Add an ingredient or a supply (F-FNB-07, F-FNB-11)',
    description: `A new stock item at zero, with its packs. Retail items are made by the server. Requires \`inventory:manage\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_MANAGE,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, body: { content: { 'application/json': { schema: CreateStockItemSchema } }, required: true } },
    responses: {
        201: jsonResponse(StockItemSchema, 'The new item.'),
        ...reads,
        404: problemResponse('No such venue.', 'not-found'),
        409: OFF,
    },
});
export const getStockItemRoute = createRoute({
    method: 'get',
    path: `${BASE}/items/{stockItemId}`,
    operationId: 'getStockItem',
    tags: ['inventory'],
    summary: 'One stock item (F-FNB-07)',
    description: 'The item with its packs, on hand and state. The ₹ figures need `inventory:reports`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: ITEM_PARAMS },
    responses: { 200: jsonResponse(StockItemSchema, 'The item.'), ...reads, 404: problemResponse('No such item at this venue.', 'not-found') },
});
export const updateStockItemRoute = createRoute({
    method: 'patch',
    path: `${BASE}/items/{stockItemId}`,
    operationId: 'updateStockItem',
    tags: ['inventory'],
    summary: 'Edit a stock item and its packs, or archive it (F-FNB-07)',
    description: `The fields given. The base unit is fixed once the item has moved (409). Requires \`inventory:manage\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_MANAGE,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: ITEM_PARAMS, body: { content: { 'application/json': { schema: UpdateStockItemSchema } }, required: true } },
    responses: {
        200: jsonResponse(StockItemSchema, 'The item as saved.'),
        ...reads,
        404: problemResponse('No such item at this venue.', 'not-found'),
        409: problemResponse('Stock tracking is off, or the base unit of an item that has moved cannot change.', 'inventory-disabled', 'conflict'),
    },
});
export const lookupStockBarcodeRoute = createRoute({
    method: 'get',
    path: `${BASE}/lookup`,
    operationId: 'lookupStockBarcode',
    tags: ['inventory'],
    summary: 'The item a scanned barcode names (F-FNB-09, F-POS-11)',
    description: 'Resolves a barcode to a stock item at the venue: a pack’s barcode (the pack too), the item’s own, or its retail ' +
        'product’s. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, query: StockLookupQuerySchema },
    responses: { 200: jsonResponse(StockLookupSchema, 'The item, and the pack.'), ...reads, 404: problemResponse('No stock item at this venue has that barcode.', 'not-found') },
});
export const listStockMovementsRoute = createRoute({
    method: 'get',
    path: `${BASE}/movements`,
    operationId: 'listStockMovements',
    tags: ['inventory'],
    summary: 'The stock movement history (F-FNB-08)',
    description: 'Newest first, cursor-paginated; one item’s with `stockItemId`; by instants (`from`, `to`) or the venue’s business ' +
        'days (`fromDay`, `toDay`). The value of each needs `inventory:reports`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, query: StockMovementListQuerySchema },
    responses: { 200: jsonResponse(StockMovementListSchema, 'A page of movements.'), ...reads, 404: problemResponse('No such venue or item.', 'not-found') },
});
export const listStockSuppliersRoute = createRoute({
    method: 'get',
    path: `${BASE}/suppliers`,
    operationId: 'listStockSuppliers',
    tags: ['inventory'],
    summary: 'The venue’s suppliers (F-FNB-09)',
    description: 'By name; archived ones with `includeArchived=true`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    request: { params: VenueScopeParamsSchema, query: StockSupplierListQuerySchema },
    responses: { 200: jsonResponse(StockSupplierListSchema, 'The suppliers.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const createStockSupplierRoute = createRoute({
    method: 'post',
    path: `${BASE}/suppliers`,
    operationId: 'createStockSupplier',
    tags: ['inventory'],
    summary: 'Add a supplier (F-FNB-09)',
    description: 'Requires `inventory:manage`. Audited.',
    [PERMISSION_EXTENSION]: INVENTORY_MANAGE,
    request: { params: VenueScopeParamsSchema, body: { content: { 'application/json': { schema: CreateStockSupplierSchema } }, required: true } },
    responses: { 201: jsonResponse(StockSupplierSchema, 'The new supplier.'), ...reads, 404: problemResponse('No such venue.', 'not-found'), 409: OFF },
});
export const updateStockSupplierRoute = createRoute({
    method: 'patch',
    path: `${BASE}/suppliers/{supplierId}`,
    operationId: 'updateStockSupplier',
    tags: ['inventory'],
    summary: 'Edit or archive a supplier (F-FNB-09)',
    description: 'The fields given; `archived: true` hides it from new deliveries. Requires `inventory:manage`. Audited.',
    [PERMISSION_EXTENSION]: INVENTORY_MANAGE,
    request: { params: SUPPLIER_PARAMS, body: { content: { 'application/json': { schema: UpdateStockSupplierSchema } }, required: true } },
    responses: { 200: jsonResponse(StockSupplierSchema, 'The supplier as saved.'), ...reads, 404: problemResponse('No such supplier at this venue.', 'not-found'), 409: OFF },
});
export const listStockReceiptsRoute = createRoute({
    method: 'get',
    path: `${BASE}/receipts`,
    operationId: 'listStockReceipts',
    tags: ['inventory'],
    summary: 'Deliveries received (F-FNB-09)',
    description: 'Newest first, cursor-paginated. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    request: { params: VenueScopeParamsSchema, query: PageQuerySchema },
    responses: { 200: jsonResponse(StockReceiptListSchema, 'A page of receipts.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const createStockReceiptRoute = createRoute({
    method: 'post',
    path: `${BASE}/receipts`,
    operationId: 'createStockReceipt',
    tags: ['inventory'],
    summary: 'Receive a delivery (F-FNB-09)',
    description: 'Each line raises its item’s stock and its weighted-average cost exactly (ADR-0040 §15). An `Idempotency-Key` is required: a ' +
        `retry answers the receipt already made. Requires \`inventory:receive\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_RECEIVE,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    request: {
        params: VenueScopeParamsSchema,
        headers: StockIdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CreateStockReceiptSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StockReceiptSchema, 'The receipt.'),
        ...reads,
        404: problemResponse('No such venue, item, pack or supplier.', 'not-found'),
        409: OFF,
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
    },
});
export const getStockReceiptRoute = createRoute({
    method: 'get',
    path: `${BASE}/receipts/{receiptId}`,
    operationId: 'getStockReceipt',
    tags: ['inventory'],
    summary: 'One delivery (F-FNB-09)',
    description: 'Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    request: { params: RECEIPT_PARAMS },
    responses: { 200: jsonResponse(StockReceiptSchema, 'The receipt.'), ...reads, 404: problemResponse('No such receipt at this venue.', 'not-found') },
});
export const recordStockUsageRoute = createRoute({
    method: 'post',
    path: `${BASE}/usage`,
    operationId: 'recordStockUsage',
    tags: ['inventory'],
    summary: 'Record waste, a staff meal or a supply issue (F-FNB-09, F-FNB-11)',
    description: 'By item, or by dish (its recipe at the venue). Waste needs a reason. An `Idempotency-Key` is required. Requires ' +
        `\`inventory:waste\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_WASTE,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: {
        params: VenueScopeParamsSchema,
        headers: StockIdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CreateStockUsageSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StockUsageResultSchema, 'The movements made.'),
        ...reads,
        404: problemResponse('No such venue, item, pack, product or station.', 'not-found'),
        409: problemResponse('Stock tracking is off, or a dish has no recipe here.', 'inventory-disabled', 'conflict'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
    },
});
export const createStockAdjustmentRoute = createRoute({
    method: 'post',
    path: `${BASE}/adjustments`,
    operationId: 'createStockAdjustment',
    tags: ['inventory'],
    summary: 'Correct one item by hand, with a note (F-FNB-09)',
    description: `An \`Idempotency-Key\` is required. Requires \`inventory:adjust\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_ADJUST,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: {
        params: VenueScopeParamsSchema,
        headers: StockIdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: CreateStockAdjustmentSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StockMovementSchema, 'The movement.'),
        ...reads,
        404: problemResponse('No such venue or item.', 'not-found'),
        409: OFF,
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
    },
});
export const listStockCountsRoute = createRoute({
    method: 'get',
    path: `${BASE}/counts`,
    operationId: 'listStockCounts',
    tags: ['inventory'],
    summary: 'Counts, open and past (F-FNB-09)',
    description: 'Newest first, cursor-paginated, without their lines. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, query: StockCountListQuerySchema },
    responses: { 200: jsonResponse(StockCountListSchema, 'A page of counts.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const createStockCountRoute = createRoute({
    method: 'post',
    path: `${BASE}/counts`,
    operationId: 'createStockCount',
    tags: ['inventory'],
    summary: 'Start a full or spot count (F-FNB-09)',
    description: `Opens a count listing its items, uncounted. Requires \`inventory:count\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_COUNT,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, body: { content: { 'application/json': { schema: CreateStockCountSchema } }, required: true } },
    responses: { 201: jsonResponse(StockCountSchema, 'The open count.'), ...reads, 404: problemResponse('No such venue or item.', 'not-found'), 409: OFF },
});
export const getStockCountRoute = createRoute({
    method: 'get',
    path: `${BASE}/counts/{countId}`,
    operationId: 'getStockCount',
    tags: ['inventory'],
    summary: 'One count, with its variance review (F-FNB-09)',
    description: 'While open, each line’s expected quantity is on hand now. The ₹ variance needs `inventory:reports`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: COUNT_PARAMS },
    responses: { 200: jsonResponse(StockCountSchema, 'The count.'), ...reads, 404: problemResponse('No such count at this venue.', 'not-found') },
});
export const putStockCountLinesRoute = createRoute({
    method: 'put',
    path: `${BASE}/counts/{countId}/lines`,
    operationId: 'putStockCountLines',
    tags: ['inventory'],
    summary: 'Enter counted quantities on an open count (F-FNB-09)',
    description: `Sets (or clears) the counted figure of the items given; other lines are kept. Requires \`inventory:count\`.${SUPPLIES_NOTE}`,
    [PERMISSION_EXTENSION]: INVENTORY_COUNT,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: COUNT_PARAMS, body: { content: { 'application/json': { schema: PutStockCountLinesSchema } }, required: true } },
    responses: {
        200: jsonResponse(StockCountSchema, 'The count as it now stands.'),
        ...reads,
        404: problemResponse('No such count, item or pack at this venue.', 'not-found'),
        409: problemResponse('Stock tracking is off, or the count is no longer open.', 'inventory-disabled', 'conflict'),
    },
});
export const submitStockCountRoute = createRoute({
    method: 'post',
    path: `${BASE}/counts/{countId}/submit`,
    operationId: 'submitStockCount',
    tags: ['inventory'],
    summary: 'Submit a count: each variance becomes a movement (F-FNB-09)',
    description: 'On hand becomes what was counted. A full count needs every line counted (`stock-count-incomplete`); a spot count ' +
        `submits its counted lines; a note may say what was found. An \`Idempotency-Key\` is required. Requires \`inventory:count\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_COUNT,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: {
        params: COUNT_PARAMS,
        headers: StockIdempotencyHeaderSchema,
        body: { content: { 'application/json': { schema: SubmitStockCountSchema } }, required: false },
    },
    responses: {
        200: jsonResponse(StockCountSchema, 'The submitted count.'),
        ...reads,
        404: problemResponse('No such count at this venue.', 'not-found'),
        409: problemResponse('Stock tracking is off, the count is not open, or a full count has uncounted items.', 'inventory-disabled', 'conflict', 'stock-count-incomplete'),
        422: problemResponse('The same Idempotency-Key was already used for a different request.', 'idempotency-key-reused'),
    },
});
export const cancelStockCountRoute = createRoute({
    method: 'post',
    path: `${BASE}/counts/{countId}/cancel`,
    operationId: 'cancelStockCount',
    tags: ['inventory'],
    summary: 'Discard an open count (F-FNB-09)',
    description: `Moves no stock. Requires \`inventory:count\`.${SUPPLIES_NOTE} Audited.`,
    [PERMISSION_EXTENSION]: INVENTORY_COUNT,
    [NARROW_PERMISSION_EXTENSION]: INVENTORY_SUPPLIES,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: COUNT_PARAMS },
    responses: {
        200: jsonResponse(StockCountSchema, 'The cancelled count.'),
        ...reads,
        404: problemResponse('No such count at this venue.', 'not-found'),
        409: problemResponse('Stock tracking is off, or the count is not open.', 'inventory-disabled', 'conflict'),
    },
});
export const listStockRecipesRoute = createRoute({
    method: 'get',
    path: `${BASE}/recipes`,
    operationId: 'listStockRecipes',
    tags: ['inventory'],
    summary: 'Every recipe at the venue (F-FNB-07)',
    description: 'By product. Costs need `inventory:reports`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema },
    responses: { 200: jsonResponse(StockRecipeListSchema, 'The recipes.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const getStockProductRecipesRoute = createRoute({
    method: 'get',
    path: `${BASE}/products/{productId}/recipes`,
    operationId: 'getStockProductRecipes',
    tags: ['inventory'],
    summary: 'A product’s recipes at the venue (F-FNB-07)',
    description: 'Its base recipe and its variants’ and modifiers’. Costs need `inventory:reports`. Requires `inventory:read`.',
    [PERMISSION_EXTENSION]: INVENTORY_READ,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: PRODUCT_PARAMS },
    responses: { 200: jsonResponse(StockProductRecipesSchema, 'The product’s recipes.'), ...reads, 404: problemResponse('No such venue or product.', 'not-found') },
});
export const putStockProductRecipesRoute = createRoute({
    method: 'put',
    path: `${BASE}/products/{productId}/recipes`,
    operationId: 'putStockProductRecipes',
    tags: ['inventory'],
    summary: 'Replace a product’s recipes at the venue (F-FNB-07)',
    description: 'Those sent are written and any other of the product’s is removed; whether it can be made is re-checked at once ' +
        '(F-FNB-10). Requires `inventory:manage`. Audited.',
    [PERMISSION_EXTENSION]: INVENTORY_MANAGE,
    [UNMASKED_BY_EXTENSION]: INVENTORY_REPORTS,
    request: { params: PRODUCT_PARAMS, body: { content: { 'application/json': { schema: PutStockProductRecipesSchema } }, required: true } },
    responses: {
        200: jsonResponse(StockProductRecipesSchema, 'The product’s recipes as saved.'),
        ...reads,
        404: problemResponse('No such venue, product, variant, modifier or item.', 'not-found'),
        409: OFF,
    },
});
export const copyStockFromVenueRoute = createRoute({
    method: 'post',
    path: `${BASE}/copy-from-venue`,
    operationId: 'copyStockFromVenue',
    tags: ['inventory'],
    summary: 'Copy another venue’s items, packs and recipes (F-FNB-13)',
    description: 'Definitions only — never quantities, costs or movements — and idempotent: retail items are never copied (each venue ' +
        'links its own), a same-name item here in the same unit is matched rather than duplicated. `dryRun: true` answers ' +
        'what it would do and writes nothing. The member must work at both venues. Requires `inventory:manage`. Audited.',
    [PERMISSION_EXTENSION]: INVENTORY_MANAGE,
    request: { params: VenueScopeParamsSchema, body: { content: { 'application/json': { schema: CopyStockFromVenueSchema } }, required: true } },
    responses: {
        200: jsonResponse(StockCopyResultSchema, 'What was copied, or would be.'),
        ...reads,
        404: problemResponse('No such venue in this organisation, or one the member does not work at.', 'not-found'),
        409: problemResponse('Stock tracking is off here, or the source is this venue.', 'inventory-disabled', 'conflict'),
    },
});
export const getStockValuationReportRoute = createRoute({
    method: 'get',
    path: `${BASE}/reports/valuation`,
    operationId: 'getStockValuationReport',
    tags: ['inventory'],
    summary: 'What the stock is worth now (F-FNB-12)',
    description: 'By item, at each item’s exact average cost. Requires `inventory:reports`.',
    [PERMISSION_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema },
    responses: { 200: jsonResponse(StockValuationReportSchema, 'The valuation.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const getStockUsageReportRoute = createRoute({
    method: 'get',
    path: `${BASE}/reports/usage`,
    operationId: 'getStockUsageReport',
    tags: ['inventory'],
    summary: 'Stock movements over a range of business days, by kind (F-FNB-12)',
    description: 'Per item: sold, refunded, wasted, staff meals, issued, received, counted and adjusted — quantity and value. Requires `inventory:reports`.',
    [PERMISSION_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, query: StockReportRangeQuerySchema },
    responses: { 200: jsonResponse(StockUsageReportSchema, 'The usage.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const getStockVarianceReportRoute = createRoute({
    method: 'get',
    path: `${BASE}/reports/variance`,
    operationId: 'getStockVarianceReport',
    tags: ['inventory'],
    summary: 'What counts found over a range of business days (F-FNB-12)',
    description: 'Each submitted count’s ₹ variance, and per item. Requires `inventory:reports`.',
    [PERMISSION_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema, query: StockReportRangeQuerySchema },
    responses: { 200: jsonResponse(StockVarianceReportSchema, 'The variance.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
export const getStockMarginsReportRoute = createRoute({
    method: 'get',
    path: `${BASE}/reports/margins`,
    operationId: 'getStockMarginsReport',
    tags: ['inventory'],
    summary: 'Cost per dish and margin per product (F-FNB-12)',
    description: 'Each product and variant with a recipe at the venue: price, price excluding tax under the venue’s tax treatment, ' +
        'recipe cost, margin. Requires `inventory:reports`.',
    [PERMISSION_EXTENSION]: INVENTORY_REPORTS,
    request: { params: VenueScopeParamsSchema },
    responses: { 200: jsonResponse(StockMarginsReportSchema, 'The margins.'), ...reads, 404: problemResponse('No such venue.', 'not-found') },
});
/** Every inventory route, in the order the document lists them. */
export const INVENTORY_ROUTES = [
    getStockStatusRoute,
    getStockAlertsRoute,
    listStockItemsRoute,
    createStockItemRoute,
    getStockItemRoute,
    updateStockItemRoute,
    lookupStockBarcodeRoute,
    listStockMovementsRoute,
    listStockSuppliersRoute,
    createStockSupplierRoute,
    updateStockSupplierRoute,
    listStockReceiptsRoute,
    createStockReceiptRoute,
    getStockReceiptRoute,
    recordStockUsageRoute,
    createStockAdjustmentRoute,
    listStockCountsRoute,
    createStockCountRoute,
    getStockCountRoute,
    putStockCountLinesRoute,
    submitStockCountRoute,
    cancelStockCountRoute,
    listStockRecipesRoute,
    getStockProductRecipesRoute,
    putStockProductRecipesRoute,
    copyStockFromVenueRoute,
    getStockValuationReportRoute,
    getStockUsageReportRoute,
    getStockVarianceReportRoute,
    getStockMarginsReportRoute,
];
//# sourceMappingURL=inventory.js.map